// components/DashboardLayout.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES, USER_ROLES } from '../utils/constants';
import { RefreshCw } from 'lucide-react';
import NotificationCenter from '../components/NotificationCenter';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import CrossAppBanner from './CrossAppBanner';
import NavCurrencySwitcher from './NavCurrencySwitcher';
import ModeSwitchOTPModal from './settings/ModeSwitchOTPModal';
import ReinstatementBanner from './account/ReinstatementBanner';
import api from '../services/api';
import '../pages/dashboard/dashboard-v2.css';
import { BannerQueueProvider, useRegisterBanner } from '../contexts/BannerQueueContext';
import { getOwnDashboardName } from '../utils/userDisplay';

const DashboardLayoutContent = ({ children }) => {
    const { user, logout } = useAuth();
    const { primary: displayName, secondary: displayHandle } = getOwnDashboardName(user);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showModeSwitchModal, setShowModeSwitchModal] = useState(false);
    const [modalTargetMode, setModalTargetMode] = useState('');
    const menuRef = useRef(null);
    const { t } = useTranslation();

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [onboardingStatus, setOnboardingStatus] = useState(null);

    const { isActive: isProfileNudgeActive } = useRegisterBanner('profileIncomplete', 2, Boolean(user && !user.profileComplete));

    const isAdmin = user?.roles?.includes('admin');

    const handleRoleSwitch = (newRole) => {
        setModalTargetMode(newRole);
        setShowModeSwitchModal(true);
        setShowUserMenu(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch onboarding status for sidebar profile strength
    useEffect(() => {
        if (!user) return;
        const fetchOnboarding = async () => {
            try {
                const res = await api.get('/onboarding/status');
                setOnboardingStatus(res.data);
            } catch (_) { }
        };
        fetchOnboarding();
    }, [user]);

    const handleLogout = () => {
        logout();
        navigate(ROUTES.HOME || '/');
    };

    if (!user) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f9fafb' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid #2563eb', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                    <p style={{ color: '#6b7280', fontSize: 14 }}>{t('loading')}</p>
                </div>
            </div>
        );
    }

    const currentRole = user?.activeRole;
    const userRoles = user?.roles || [];
    const profileUrl = '/profile/' + (user?.username || '');

    const isGiggerMode = currentRole === 'gigger' || (userRoles.includes('gigger') && currentRole === 'gigger');
    const targetMode = currentRole === 'gigger' ? 'shipper' : 'gigger';

    const isRouteActive = (to, exact = false) => {
        if (exact) return pathname === to;
        return pathname === to || (to !== '/dashboard' && pathname.startsWith(to + '/'));
    };

    // Compute progress details for the sidebar
    const checklistItems = [
        { id: 'email', completed: !!user.emailVerified },
        { id: 'phone', completed: !!user.phoneVerified },
        { id: 'profile', completed: !!(onboardingStatus?.onboardingProgress?.step1) },
        { id: 'listing', completed: !!(onboardingStatus?.onboardingProgress?.step2) },
        { id: 'verify', completed: !!(onboardingStatus?.onboardingProgress?.step3 || user.skipVerificationAt || user.identityVerified) }
    ];
    const completedCount = checklistItems.filter(i => i.completed).length;
    const progressPct = onboardingStatus?.profileStrength != null
        ? onboardingStatus.profileStrength
        : Math.round((completedCount / checklistItems.length) * 100);

    // ── Financial Ban Gate ──────────────────────────────────────────────────
    // If a user was banned for commission default and hasn't reinstated,
    // show the reinstatement portal instead of the normal dashboard.
    const isFinanciallyBanned =
        user?.accountStatus === 'banned' &&
        user?.bannedReason === 'repeated_commission_default';

    if (isFinanciallyBanned) {
        return (
            <ReinstatementBanner
                user={user}
                walletBalance={user?.wallet?.availableBalance || 0}
                onReinstatementSuccess={() => window.location.reload()}
            />
        );
    }
    // ───────────────────────────────────────────────────────────────────────

    return (
        <div className="v2-shell">
            {/* ── CrossAppBanner rendered inside the shell so it participates in flex layout ── */}
            <div className="v2-banner-slot">
                <CrossAppBanner />
            </div>

            {/* ── Hamburger (mobile) — inside .v2-shell so the CSS selector matches ── */}
            <button className="hamburger" onClick={() => setSidebarOpen(s => !s)} aria-label="Open menu">☰</button>

            {/* ── Overlay (mobile sidebar backdrop) ── */}
            {sidebarOpen && <div className="overlay show" onClick={() => setSidebarOpen(false)} />}

            {/* ═══════════ SIDEBAR ═══════════ */}
            <div className="v2-body">
            <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>

                <div className="sb-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
                    <img src="/gigship-logo-dashboard.png" alt="GigShip" className="sb-logo-img" />
                </div>

                <div className="sb-user">
                    <div className="sb-user-top">
                        <div className="avatar">
                            {user.profilePicture
                                ? <img src={user.profilePicture} alt={user.username} />
                                : user.username?.[0]?.toUpperCase()}
                        </div>
                        <div className="user-meta">
                            <div className="user-name">{displayName}</div>
                            {displayHandle && <div className="user-handle">{displayHandle}</div>}
                        </div>
                    </div>
                    <div className="mode-badge" onClick={() => handleRoleSwitch(targetMode)}>
                        <div className="mode-dot" />
                        Mode: {user.activeRole === USER_ROLES.SHIPPER ? 'Shipper' : user.activeRole === USER_ROLES.HYBRID ? 'Hybrid' : 'Gigger'}
                    </div>
                </div>

                <div className="sb-progress">
                    <div className="sb-prog-label">
                        <span>Profile strength</span>
                        <span className="sb-prog-pct">{progressPct}%</span>
                    </div>
                    <div className="prog-track">
                        <div className="prog-fill" style={{ width: `${progressPct}%` }} />
                    </div>
                </div>

                {/* Scrollable nav + bottom actions in one unit */}
                <div className="sb-scroll-wrap">
                    <nav className="sb-nav">
                        <div className="nav-section">Main</div>
                        <Link to="/dashboard" className={`nav-item${isRouteActive('/dashboard', true) ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">🏠</span>{t('dashboard.title', 'Home')}
                        </Link>
                        {(user.activeRole === USER_ROLES.GIGGER || user.activeRole === USER_ROLES.HYBRID) && (
                            <Link to="/dashboard/gigs" className={`nav-item${isRouteActive('/dashboard/gigs') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                                <span className="ni">💼</span>{t('my_gigs', 'My Gigs')}
                            </Link>
                        )}
                        {(user.activeRole === USER_ROLES.SHIPPER || user.activeRole === USER_ROLES.HYBRID) && (
                            <Link to="/dashboard/requests" className={`nav-item${isRouteActive('/dashboard/requests') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                                <span className="ni">📦</span>{t('my_requests', 'My Requests')}
                            </Link>
                        )}
                        <Link to="/requests" className={`nav-item${isRouteActive('/requests') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">🔍</span>Browse Requests
                        </Link>
                        <Link to="/dashboard/messages" className={`nav-item${isRouteActive('/dashboard/messages') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">💬</span>{t('messages', 'Messages')}
                        </Link>
                        <Link to="/dashboard/orders" className={`nav-item${isRouteActive('/dashboard/orders') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">📦</span>{t('orders.title', 'Orders')}
                        </Link>

                        <div className="nav-section">Account</div>
                        <Link to={profileUrl} className={`nav-item${isRouteActive(profileUrl) ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">👤</span>{t('leaderboard.view_profile', 'View Profile')}
                        </Link>
                        <Link to="/dashboard/reviews" className={`nav-item${isRouteActive('/dashboard/reviews') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">⭐</span>Reviews
                        </Link>
                        <Link to="/dashboard/wallet" className={`nav-item${isRouteActive('/dashboard/wallet') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">💰</span>{t('wallet.title', 'Earnings')}
                        </Link>
                        <Link to="/dashboard/referrals" className={`nav-item${isRouteActive('/dashboard/referrals') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">💳</span>{t('referrals.title', 'Referrals')}
                        </Link>
                        <Link to="/dashboard/analytics" className={`nav-item${isRouteActive('/dashboard/analytics') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">📊</span>{t('analytics.title', 'Analytics')}
                        </Link>

                        <div className="nav-section">Grow</div>
                        <Link to="/dashboard/forum" className={`nav-item${isRouteActive('/dashboard/forum') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">🗣️</span>{isGiggerMode ? 'Gigger Forum' : 'Shipper Forum'}
                        </Link>
                        {user.activeRole === USER_ROLES.GIGGER && (
                            <Link to="/saved-requests" className={`nav-item${isRouteActive('/saved-requests') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                                <span className="ni">🏷️</span>Saved Requests
                            </Link>
                        )}
                        {user.activeRole === USER_ROLES.SHIPPER && (
                            <Link to="/saved-gigs" className={`nav-item${isRouteActive('/saved-gigs') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                                <span className="ni">🏷️</span>Saved Gigs
                            </Link>
                        )}

                        {isAdmin && (
                            <>
                                <div className="nav-section">Admin</div>
                                <Link to="/admin" className={`nav-item${isRouteActive('/admin') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                                    <span className="ni">🛡️</span>Admin Dashboard
                                </Link>
                            </>
                        )}
                    </nav>

                    {/* Bottom actions flow after nav items within the same scroll container */}
                    <div className="sb-bottom">
                        <Link to="/dashboard/settings" className={`nav-item${isRouteActive('/dashboard/settings') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">⚙️</span>{t('settings.title', 'Settings')}
                        </Link>
                        <Link to="/support" className={`nav-item${isRouteActive('/support') ? ' active' : ''}`} onClick={() => setSidebarOpen(false)}>
                            <span className="ni">❓</span>Support
                        </Link>
                        <button className="nav-item" onClick={handleLogout}>
                            <span className="ni">🚪</span>{t('logout', 'Sign Out')}
                        </button>
                    </div>
                </div>

            </aside>

            {/* ═══════════ MAIN ═══════════ */}
            <div className="main">

                <div className="topbar">
                    <div className="topbar-left">
                        {/* Hide greeting on very small screens using v2 CSS class, not Tailwind */}
                        <div className="topbar-greeting">
                            <h1>Hello, {displayName} 👋</h1>
                            <div className="date-sub">Manage your GigShip experience</div>
                        </div>
                    </div>
                    <div className="topbar-right">
                        <LanguageSwitcher variant="compact" />
                        <NavCurrencySwitcher />
                        <NotificationCenter />
                        {/* User avatar + dropdown — scoped classes replace Tailwind utilities */}
                        <div className="v2-user-menu-wrap" ref={menuRef}>
                            <div
                                className="topbar-avatar"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setShowUserMenu(!showUserMenu)}
                                aria-label="User menu"
                            >
                                {user.profilePicture
                                    ? <img src={user.profilePicture} alt={user.username} />
                                    : user.username?.[0]?.toUpperCase()}
                            </div>
                            {showUserMenu && (
                                <div className="v2-dropdown">
                                    <div className="v2-dropdown-header">
                                        <p className="v2-dropdown-name">{displayName}</p>
                                        {displayHandle && <p className="v2-dropdown-handle">{displayHandle}</p>}
                                    </div>
                                    <div
                                        className="v2-dropdown-item v2-dropdown-item--switch"
                                        onClick={() => handleRoleSwitch(targetMode)}
                                    >
                                        <span className="v2-dropdown-hint">{t('switch_to')}:</span>
                                        <span className="v2-dropdown-switch-label">
                                            <RefreshCw style={{ width: 14, height: 14, display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                                            {user.activeRole === 'gigger' ? 'Shipper Mode' : 'Gigger Mode'}
                                        </span>
                                    </div>
                                    <div className="v2-dropdown-item">
                                        <button className="v2-dropdown-logout" onClick={handleLogout}>
                                            {t('logout')}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="content">
                    {/* Incomplete Profile Banner */}
                    {(user && !user.profileComplete && isProfileNudgeActive) && (
                        <div className="v2-profile-nudge">
                            <p>{t('profile_incomplete_message', 'Complete your profile setups.')}</p>
                            <Link to="/complete-profile">
                                {t('complete_profile', 'Complete Profile')} →
                            </Link>
                        </div>
                    )}
                    {children}
                </div>
            </div>

            </div>{/* end .v2-body */}

            <ModeSwitchOTPModal
                isOpen={showModeSwitchModal}
                onClose={() => setShowModeSwitchModal(false)}
                targetMode={modalTargetMode}
                onSuccess={() => setShowModeSwitchModal(false)}
            />
        </div>
    );
};

const DashboardLayout = ({ children }) => {
    return (
        <BannerQueueProvider>
            <DashboardLayoutContent>{children}</DashboardLayoutContent>
        </BannerQueueProvider>
    );
};

export default DashboardLayout;
